// Statistiche del negozio: eventi anonimi inviati al backend (POST /api/shops/track).
// Nessun cookie e nessun dato personale: un identificativo casuale per visita,
// tenuto in sessionStorage e rinnovato dopo 30 minuti di inattività.
const API_URL = process.env.REACT_APP_BACKEND_URL;
const VISIT_KEY = 'rarifex_shop_visit';
const ENTRY_KEY = 'rarifex_shop_entry';
const IDLE_MS = 30 * 60 * 1000;

// Pagine della bottega tracciate (segmento dopo /artigiano/<slug>/)
const PAGES = ['prodotti', 'su-misura', 'laboratorio', 'portfolio', 'recensioni', 'contatti', 'spedizioni-resi'];

const REFERRERS = [
  ['instagram.', 'instagram'], ['facebook.', 'facebook'], ['fb.', 'facebook'], ['tiktok.', 'tiktok'],
  ['pinterest.', 'pinterest'], ['google.', 'google'],
];

const safe = (fn, fallback = null) => {
  try { return fn(); } catch (_) { return fallback; }
};

const referrerSource = () => {
  const ref = safe(() => document.referrer, '') || '';
  if (!ref) return null;
  const host = safe(() => new URL(ref).hostname.toLowerCase(), '');
  if (!host || host === window.location.hostname) return null;
  const hit = REFERRERS.find(([needle]) => host.includes(needle));
  return hit ? hit[1] : null;
};

const randomId = () => {
  const bytes = new Uint8Array(12);
  safe(() => window.crypto.getRandomValues(bytes));
  const rnd = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${Date.now().toString(36)}${rnd}`.slice(0, 32);
};

// Provenienza dell'ingresso, letta dai parametri prima che vengano tolti dall'indirizzo:
// ?via=<etichetta> (es. qr) > social di provenienza > link della bottega > marketplace > diretto
export const rememberShopEntry = (slug, params) => {
  const via = (params.get('via') || '').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 24);
  const source = via || referrerSource() || (params.get('ref') ? 'link' : params.has('negozio') ? 'marketplace' : 'direct');
  safe(() => sessionStorage.setItem(ENTRY_KEY, JSON.stringify({ slug, source })));
};

const send = (payload) => {
  if (!API_URL) return;
  const url = `${API_URL}/api/shops/track`;
  const body = JSON.stringify(payload);
  // text/plain: richiesta "semplice", nessun preflight CORS
  const sent = safe(() => navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' })), false);
  if (!sent) {
    safe(() => fetch(url, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'text/plain' } }).catch(() => {}));
  }
};

// Visita in corso per questa bottega (ne apre una nuova se serve)
const ensureVisit = (crafterId, slug) => {
  const now = Date.now();
  const visit = safe(() => JSON.parse(sessionStorage.getItem(VISIT_KEY) || 'null'));
  if (visit && visit.crafterId === crafterId && now - visit.last < IDLE_MS) {
    visit.last = now;
    safe(() => sessionStorage.setItem(VISIT_KEY, JSON.stringify(visit)));
    return visit.sid;
  }
  const entry = safe(() => JSON.parse(sessionStorage.getItem(ENTRY_KEY) || 'null'));
  const source = (entry && entry.slug === slug && entry.source) || referrerSource() || 'direct';
  safe(() => sessionStorage.removeItem(ENTRY_KEY));
  const sid = randomId();
  safe(() => sessionStorage.setItem(VISIT_KEY, JSON.stringify({ crafterId, sid, last: now })));
  send({ crafter_id: crafterId, sid, kind: 'visit', source });
  return sid;
};

// Evento per il percorso corrente del negozio
export const trackShopPath = ({ crafterId, slug, pathname, embedded, products }) => {
  const sid = ensureVisit(crafterId, slug);
  if (embedded) {
    if (pathname === '/cart') send({ crafter_id: crafterId, sid, kind: 'cart' });
    return;
  }
  const rest = pathname.replace(new RegExp(`^/artigiano/${slug}/?`, 'i'), '').replace(/\/$/, '');
  if (!rest) {
    send({ crafter_id: crafterId, sid, kind: 'page', page: 'home' });
    return;
  }
  if (rest === 'collezioni' || rest.startsWith('collezioni/')) {
    send({ crafter_id: crafterId, sid, kind: 'page', page: 'collezioni' });
    return;
  }
  if (PAGES.includes(rest)) {
    send({ crafter_id: crafterId, sid, kind: 'page', page: rest });
    return;
  }
  const m = rest.match(/^prodotto\/([^/]+)$/);
  if (m) {
    const key = decodeURIComponent(m[1]);
    const product = (products || []).find((p) => p.slug === key || p.id === key);
    if (product) send({ crafter_id: crafterId, sid, kind: 'product', product_id: product.id });
  }
};
